import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { createAppTheme } from '../theme';
import { DEFAULT_PORTAL_MODE, getPortalTokens } from '../styles/portalTheme';
import {
  applyPortalColorMode,
  persistPortalColorMode,
  PORTAL_COLOR_MODE_RESET_EVENT,
  readStoredPortalColorMode,
} from '../styles/portalColorMode';
import {
  resolveThemeSwitchOrigin,
  runThemeSwitchTransition,
} from '../styles/runThemeSwitchTransition';
import { useAuth } from './AuthContext';

const ThemeModeContext = createContext(null);

export const useThemeMode = () => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeModeProvider');
  }
  return context;
};

export const ThemeModeProvider = ({ children }) => {
  const { userRole, loading } = useAuth();
  const [mode, setModeState] = useState(readStoredPortalColorMode);

  const setMode = useCallback((nextMode) => {
    const next = nextMode === 'dark' ? 'dark' : DEFAULT_PORTAL_MODE;
    setModeState(next);
    persistPortalColorMode(next);
    applyPortalColorMode(next);
  }, []);

  const toggleMode = useCallback(
    (originOrEvent) => {
      const next = mode === 'dark' ? DEFAULT_PORTAL_MODE : 'dark';
      const currentTokens = getPortalTokens(mode);
      const nextTokens = getPortalTokens(next);
      runThemeSwitchTransition({
        origin: resolveThemeSwitchOrigin(originOrEvent),
        previousPageBg: currentTokens.pageBg,
        nextPageBg: nextTokens.pageBg,
        accent: nextTokens.accent,
        apply: () => setMode(next),
      });
    },
    [mode, setMode],
  );

  useEffect(() => {
    applyPortalColorMode(mode);
  }, [mode]);

  useEffect(() => {
    const handleReset = () => {
      setModeState(DEFAULT_PORTAL_MODE);
      applyPortalColorMode(DEFAULT_PORTAL_MODE);
    };
    window.addEventListener(PORTAL_COLOR_MODE_RESET_EVENT, handleReset);
    return () => window.removeEventListener(PORTAL_COLOR_MODE_RESET_EVENT, handleReset);
  }, []);

  useEffect(() => {
    if (loading || userRole) return;
    setMode(DEFAULT_PORTAL_MODE);
  }, [loading, userRole, setMode]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const value = useMemo(
    () => ({
      mode,
      isDarkMode: mode === 'dark',
      setMode,
      toggleMode,
    }),
    [mode, setMode, toggleMode],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
